import React, { FunctionComponent, useContext } from "react";
import { useForm } from "react-hook-form";
import { Form, FormActions, FormFields, FormTextField } from "sprint-common-web";

import { AppConfigContext } from "../../../core/configuration/Configuration";
import { useDeliveryBucketsApi } from "../api/useDeliveryBucketsApi";
import { useInviteToDeliveryBucketForm } from "./useInviteToDeliveryBucketForm";

export interface InviteToDeliveryBucketFormProps {
    deliveryBucketId: string;
    onCancel(): void;
    onSubmit(): void;
}

interface InviteToDeliveryBucketFormInputs {
    email: string;
    subject: string;
    body: string;
}

export const InviteToDeliveryBucketForm: FunctionComponent<InviteToDeliveryBucketFormProps> = ({
    deliveryBucketId,
    onSubmit,
    onCancel
}: InviteToDeliveryBucketFormProps) => {
    const { inviteToDeliveryBucket } = useDeliveryBucketsApi();
    const { validateBody } = useInviteToDeliveryBucketForm();
    const { bucketInvitationEmailConfig } = useContext(AppConfigContext);

    const { errors, control, formState, reset, handleSubmit } = useForm<InviteToDeliveryBucketFormInputs>({
        defaultValues: {
            email: "",
            subject: bucketInvitationEmailConfig.subject,
            body: bucketInvitationEmailConfig.body
        },
        criteriaMode: "all"
    });

    const handleInviteToBucket = async ({ email, subject, body }: InviteToDeliveryBucketFormInputs) => {
        await inviteToDeliveryBucket({ recipientEmail: email, body, subject, deliveryBucketId });
        onSubmit();

        reset();
    };

    const handleCancel = () => {
        reset();

        onCancel();
    };

    return (
        <Form onSubmit={handleSubmit(handleInviteToBucket)}>
            <FormFields>
                <FormTextField
                    name="email"
                    label="Email"
                    control={control}
                    errors={errors}
                    rules={{
                        required: "Enter email",
                        pattern: {
                            value: /^[^@]+@{1}[^\\.]+\..+/i,
                            message: "This is not a valid email address"
                        }
                    }}
                />
                <FormTextField
                    name="subject"
                    label="Subject"
                    control={control}
                    errors={errors}
                    rules={{
                        required: "Enter subject"
                    }}
                />
                <FormTextField
                    name="body"
                    label="Body"
                    control={control}
                    errors={errors}
                    multiline
                    rules={{
                        required: "Enter body",
                        validate: validateBody
                    }}
                    tooltipContent="Required token: {link} - will be replaced by link"
                />
            </FormFields>
            <FormActions isLoading={formState.isSubmitting || formState.isValidating} onCancel={handleCancel} />
        </Form>
    );
};
